import { ChangeDetectionStrategy, Component, LOCALE_ID, computed, inject, input } from '@angular/core';
import { formatDate, formatNumber } from '@angular/common';

/**
 * Single rendering of a position for the workforce screens: the position local identifier (PLI) in a bordered chip
 * so its end is unambiguous, followed by only the optional details the caller supplies, always in the same order
 * (description, FTE, open date). Omit or pass null for any detail that should not appear.
 */
@Component({
  selector: 'cert-workforce-position-label',
  template: ` <span class="fw-bold">[{{ displayIdentifier() }}]</span>
    @if (details()) {
      <span>&ngsp;·&ngsp;{{ details() }}</span>
    }`,
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class WorkforcePositionLabelComponent {
  readonly identifier = input<string | null | undefined>();
  // Used as the identifier text when the position has no PLI.
  readonly positionId = input<number | null | undefined>();
  readonly description = input<string | null | undefined>();
  readonly fte = input<number | null | undefined>();
  readonly openDate = input<Date | string | null | undefined>();

  private readonly locale = inject(LOCALE_ID);

  readonly displayIdentifier = computed(() => {
    const identifier = this.identifier();
    if (identifier) return identifier;
    const positionId = this.positionId();
    return positionId === null || positionId === undefined ? '' : `Position ${positionId}`;
  });

  readonly details = computed(() => {
    const parts: string[] = [];
    const description = this.description();
    const fte = this.fte();
    const openDate = this.openDate();

    if (description) parts.push(description);
    if (fte !== null && fte !== undefined) parts.push(`${formatNumber(fte, this.locale, '1.0-2')} FTE`);
    if (openDate) parts.push(`Opened ${formatDate(openDate, 'MM/dd/yyyy', this.locale)}`);

    return parts.join(' · ');
  });
}
